import { VisuallyHidden, unstable_useId as useId } from "reakit";
import type { ComponentPropsWithoutRef, MouseEvent } from "react";
import { forwardRef, useContext, useMemo } from "react";
import { Cross, Clock } from "@mos-cat/ds-icons";
import type { SystemStyleObject } from "@styled-system/css";
import { pxToRem } from "@mos-cat/ds-theme";
import { useRegisterRow } from "../internal/useRegisterRow";
import { Box } from "../../Box/Box";
import { Text } from "../../Text/Text";
import { Row } from "../internal/Row";
import { UtilityButton } from "../../UtilityButton/UtilityButton";
import type { OptionParams } from "../internal/types";
import { createLanguageGetter } from "../internal/text";
import { searchContext } from "../internal/context";
import { WHCM, WHCMstyles } from "../../constants";

type HistoryOptionProps = Omit<ComponentPropsWithoutRef<"div">, "role"> & {
  value: string;
};

const deleteButtonStyles: SystemStyleObject = {
  p: pxToRem(5),
  borderRadius: "button",
  border: 1,
  borderColor: "transparent",
  color: "gray-90",
  ':hover, &[data-virtuallyfocused="true"]': {
    bg: "gray-15",
    borderColor: "gray-15",
  },
  ":active": {
    bg: "gray-20",
    borderColor: "gray-20",
  },
  [WHCM]: {
    ...WHCMstyles,
    color: "WindowText",
    '[data-highlighted="true"] &, [class*="SearchFieldRow"]:hover &': {
      color: "HighlightText",
      '&:hover, &[data-virtuallyfocused="true"]': {
        borderColor: "Background",
        backgroundColor: "Background",
        color: "WindowText",
      },
    },
  },
};

/**
 * @todo Сделать рефакторинг, чтобы можно было добавлять свои опции с кнопкой
 */
export const HistoryOption = forwardRef<HTMLDivElement, HistoryOptionProps>(function HistoryOption(
  { children, value, ...props },
  ref
) {
  const { id } = useId(props) as { id: string };
  const cellIds: OptionParams["cellIds"] = useMemo<OptionParams["cellIds"]>(
    function getCellIds() {
      return [`${id}-content`, `${id}-action`];
    },
    [id]
  );
  useRegisterRow({
    type: "actionable",
    id,
    cellIds,
    value,
  });
  const { lang, activeDescendant, send } = useContext(searchContext);
  const getText = useMemo(() => createLanguageGetter(lang), [lang]);
  return (
    <Row
      ref={ref}
      {...props}
      id={id}
      pr="sp-8"
      onMouseUp={(event: MouseEvent<HTMLDivElement>) => {
        props.onMouseUp?.(event);
        if (event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        send({ type: "select", id });
      }}
    >
      <Box py="sp-4">
        <Text color="gray-65">
          <Box as={Clock} my="sp-2" size="1.25rem" />
        </Text>
      </Box>
      <Box role="gridcell">
        <Box id={cellIds[0]}>
          <Text as="button" textAlign="left" color="linkVisitedColor">
            {children}
          </Text>
        </Box>
      </Box>
      <Box role="gridcell" ml="auto" styles={{ label: "OptionAction" }}>
        <UtilityButton
          onMouseUpCapture={(event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            send({ type: "delete", id });
          }}
          onClick={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
          id={cellIds[1]}
          data-virtuallyfocused={activeDescendant === cellIds[1]}
          styles={deleteButtonStyles}
        >
          <Cross width={pxToRem(20)} height={pxToRem(20)} />
          <VisuallyHidden>{getText("deleteFromHistory")}</VisuallyHidden>
        </UtilityButton>
      </Box>
    </Row>
  );
});

HistoryOption.displayName = "HistoryOption";
